import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import LanguageContext from '../contexts/LanguageContext';
import { getTranslation } from '../services/languageService';
import Markdown from '../components/Markdown';
import { getFeaturedProducts } from '../services/productService';
import { getEnabledCategories } from '../services/categoryService';
import shouldUseMockData from '../services/apiConfig';
import './Home.scss';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const Home = () => {
  const { language } = useContext(LanguageContext);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [displaySettings, setDisplaySettings] = useState({});

  // Load categories from API, fall back to JSON data
  useEffect(() => {
    let cancelled = false;

    const loadCategories = async () => {
      if (shouldUseMockData()) {
        if (!cancelled) setCategories(getEnabledCategories(language));
        setCategoriesLoading(false);
        return;
      }
      setCategoriesLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/categories?lang=${language}`);
        if (response.ok) {
          const data = await response.json();
          if (!cancelled) {
            // Only show enabled categories
            const enabled = data.filter(cat => cat.enabled !== false);
            const mapped = enabled.map(cat => ({
              id: cat.key,
              title: cat.label,
              description: cat.description,
              icon: cat.icon || '📋',
              link: `/services/${cat.slug || cat.key}`
            }));
            setCategories(mapped);
            setCategoriesLoading(false);
          }
          return;
        }
      } catch (error) {
        console.warn('Failed to load categories from API:', error);
      }
      if (!cancelled) {
        setCategories(getEnabledCategories(language));
      }
      setCategoriesLoading(false);
    };

    loadCategories();
    return () => { cancelled = true; };
  }, [language]);

  // Load display settings
  useEffect(() => {
    const load = async () => {
      if (shouldUseMockData()) return;
      try {
        const resp = await fetch(`${API_BASE_URL}/api/settings?lang=${language}`);
        if (resp.ok) {
          const data = await resp.json();
          setDisplaySettings(data.display || {});
        }
      } catch (err) {
        // Silently fall back — testimonials stay visible by default
      }
    };
    load();
  }, [language]);

  // Load featured products
  useEffect(() => {
    const featured = getFeaturedProducts(6, language);
    setFeaturedProducts(featured);
    setActiveIndex(0);
  }, [language]);

  // Featured products carousel — auto-cycle every 5 seconds
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (featuredProducts.length <= 1 || isPaused) return;
    const interval = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % featuredProducts.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [featuredProducts.length, isPaused]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  // Statistics data
  const statistics = [
    { number: '2+', label: getTranslation('home.statistics.clients', language) },
    { number: '1+', label: getTranslation('home.statistics.projects', language) },
    { number: '20+', label: getTranslation('home.statistics.experience', language) },
    { number: '3+', label: getTranslation('home.statistics.team', language) }
  ];

  return (
    <motion.div
      className="home"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <motion.div className="hero-content" variants={itemVariants}>
            <h1 className="hero-title">{getTranslation('home.hero.title', language)}</h1>
            <p className="hero-subtitle">{getTranslation('home.hero.subtitle', language)}</p>
            <div className="hero-buttons">
              <Link to="/services" className="btn">
                {getTranslation('home.hero.ctaPrimary', language)}
              </Link>
              <Link to="/portfolio" className="btn btn-outline">
                {getTranslation('home.hero.ctaSecondary', language)}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section services">
        <div className="container">
          <motion.h2 className="section-title text-center" variants={itemVariants}>
            {getTranslation('home.services.title', language)}
          </motion.h2>
          <div className="services-grid">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                className="service-card card"
                variants={itemVariants}
                whileHover={{ y: -10 }}
              >
                <Link to={category.link} className="service-link">
                  <div className="service-icon">{category.icon}</div>
                  <h3 className="service-title">{category.title}</h3>
                  <Markdown className="service-description">{category.description}</Markdown>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      {featuredProducts.length > 0 && (
        <section className="section featured-products bg-secondary">
          <div className="container">
            <motion.h2 className="section-title text-center" variants={itemVariants}>
              {getTranslation('home.featured.title', language)}
            </motion.h2>
            <motion.p className="section-subtitle text-center" variants={itemVariants}>
              {getTranslation('home.featured.subtitle', language)}
            </motion.p>
            <div
              className="featured-carousel"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              <div className="featured-carousel-track">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={featuredProducts[activeIndex].id}
                    initial={{ opacity: 0, x: 60 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -60 }}
                    transition={{ duration: 0.35 }}
                    className="featured-carousel-slide"
                  >
                    <Link
                      to={`/products/${featuredProducts[activeIndex].id}`}
                      className="featured-minimal-card"
                    >
                      <div className="featured-minimal-image">
                        {featuredProducts[activeIndex].images?.[0] ? (
                          <img
                            src={featuredProducts[activeIndex].images[0]}
                            alt={featuredProducts[activeIndex].name}
                            loading="lazy"
                          />
                        ) : (
                          <div className="featured-minimal-placeholder" />
                        )}
                      </div>
                      <h3 className="featured-minimal-title">
                        {featuredProducts[activeIndex].name}
                      </h3>
                    </Link>
                  </motion.div>
                </AnimatePresence>
              </div>
              <div className="carousel-dots">
                {featuredProducts.map((_, index) => (
                  <button
                    key={index}
                    className={`carousel-dot ${index === activeIndex ? 'active' : ''}`}
                    onClick={() => setActiveIndex(index)}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
            <motion.div className="text-center" variants={itemVariants}>
              <Link to="/products" className="btn btn-outline">
                {getTranslation('home.featured.viewAll', language)}
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* Statistics Section */}
      <section className="section statistics bg-secondary">
        <div className="container">
          <div className="statistics-grid">
            {statistics.map((stat, index) => (
              <motion.div
                key={index}
                className="statistic-item"
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
              >
                <div className="statistic-number">{stat.number}</div>
                <div className="statistic-label">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {displaySettings.showHomeTestimonials !== false && (
        <section className="section testimonials">
        <div className="container">
          <motion.h2 className="section-title text-center" variants={itemVariants}>
            {getTranslation('home.testimonials.title', language)}
          </motion.h2>
          <div className="testimonials-grid">
            <motion.div className="testimonial-card card" variants={itemVariants}>
              <p className="testimonial-quote">"{getTranslation('home.testimonials.client1.quote', language)}"</p>
              <p className="testimonial-author">{getTranslation('home.testimonials.client1.author', language)}</p>
            </motion.div>
          </div>
        </div>
      </section>
      )}

      {/* Why Choose Us Section */}
      <section className="section why-choose-us bg-secondary">
        <div className="container">
          <motion.h2 className="section-title text-center" variants={itemVariants}>
            {getTranslation('home.whyChooseUs.title', language)}
          </motion.h2>
          <div className="features-grid">
            <motion.div className="feature-card card" variants={itemVariants}>
              <h3 className="feature-title">{getTranslation('home.whyChooseUs.features.0.title', language)}</h3>
              <p className="feature-description">{getTranslation('home.whyChooseUs.features.0.description', language)}</p>
            </motion.div>
            <motion.div className="feature-card card" variants={itemVariants}>
              <h3 className="feature-title">{getTranslation('home.whyChooseUs.features.1.title', language)}</h3>
              <p className="feature-description">{getTranslation('home.whyChooseUs.features.1.description', language)}</p>
            </motion.div>
            <motion.div className="feature-card card" variants={itemVariants}>
              <h3 className="feature-title">{getTranslation('home.whyChooseUs.features.2.title', language)}</h3>
              <p className="feature-description">{getTranslation('home.whyChooseUs.features.2.description', language)}</p>
            </motion.div>
            <motion.div className="feature-card card" variants={itemVariants}>
              <h3 className="feature-title">{getTranslation('home.whyChooseUs.features.3.title', language)}</h3>
              <p className="feature-description">{getTranslation('home.whyChooseUs.features.3.description', language)}</p>
            </motion.div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default Home;
